import { useIntl } from 'react-intl'
import { messages } from '../messages'
import { filterRepeatedEntries, processValues } from './utils'
import { IconArrowLineUp, Button } from '@vtex/admin-ui'
import { useMemo, useRef } from 'react'
import { validationSuite } from './validation'
import { FileInput } from '../../../../FileInput'
import { INVALID_FORMAT_MESSAGE_ERROR } from '../../../../FileInput/utils'
import { BIN_SUB_FORM_NAME } from '../BINEditor'
import { FormState, useWatch } from '@vtex/admin-ui-form'

type Props = {
  state: FormState
}

export function BinFileInput({ state }: Props) {
  const intl = useIntl()
  const { formatMessage: i18n } = intl
  const inputRef = useRef<HTMLInputElement>(null)

  const setErrorMessage = (message: string) =>
    state.setError(BIN_SUB_FORM_NAME, { message })

  const values = useWatch({ form: state, name: BIN_SUB_FORM_NAME })

  const validation = useMemo(
    () => validationSuite(intl, values.length),
    [intl, values]
  )

  const handleCatchError = (err: unknown) => {
    if (err instanceof Error && err.message === INVALID_FORMAT_MESSAGE_ERROR) {
      setErrorMessage(i18n(messages.fileInput.validation.wrongFormat))
    } else {
      setErrorMessage(i18n(messages.fileInput.validation.genericError))
    }
  }

  const handleValidation = (entries: string[]) => {
    const uniqueEntries = filterRepeatedEntries(entries, values)
    const result = validation(uniqueEntries)
    if (!result.valid) {
      result.errors[0].message && setErrorMessage(result.errors[0].message)
      return
    }
    state.setValue(BIN_SUB_FORM_NAME, [...values, ...uniqueEntries])
    state.clearErrors(BIN_SUB_FORM_NAME)
  }

  const handleChange = async (data: string) => {
    try {
      handleValidation(processValues(data))
    } catch (err) {
      handleCatchError(err)
    }
  }

  const openInput = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
      inputRef.current?.click()
    }
  }

  return (
    <FileInput
      id="bin-input-file"
      helpText={i18n(messages.fileInput.helpText)}
      onChange={handleChange}
      onError={handleCatchError}
    >
      <Button
        variant="neutralTertiary"
        icon={<IconArrowLineUp />}
        onClick={openInput}
      >
        {i18n(messages.fileInput.label)}
      </Button>
    </FileInput>
  )
}
